// Does a covered life already have a claim in flight?
//
// A second claim on the same member is accepted and only caught at assessment,
// where the benefit amount silently zeroes out. By then the file has been built for
// nothing. The customer-facing journey has blocked this at member selection since
// ISS-046; ASPin had no equivalent, so the same member could be logged twice
// (OMK individual retest, item 19).
//
// This is a convenience guard in front of the engine's own duplicate handling, not
// the guard itself: with nothing to match on it returns null (does not block), so a
// missing identifier never strands a genuine claim.
//
// No optional chaining / ES6+ syntax the project's Babel config rejects.

import {
  isAnyRejection,
  isFinalRejection,
  isPendingRejection
} from './claimRejection.es6';

/**
 * Statuses after which a fresh claim on the same life is legitimate — the previous
 * attempt did not consume the benefit. Anything else (pending, in assessment,
 * approved, paid) means a second claim is a duplicate.
 *
 * Rejections are not on this list: whether one closes the member depends on the
 * partner and on how final it is, which claimBlocks decides.
 */
const RECLAIMABLE = [
  'cancel',
  'withdraw',
  'void',
  'expire',
  // A discarded claim was abandoned before assessment and never touched the
  // benefit, so it must not stand in the way of a genuine one. It is a status this
  // product actually issues (ClaimStatusEnum.Discarded) and leaving it out of this
  // list stranded the covered life permanently.
  'discard'
];

/**
 * Has this claim already been approved (or approved and paid)?
 *
 * Kept separate from claimBlocks because the two are not the same block: a claim
 * still in flight can be tracked or discarded, whereas an approved claim has
 * consumed the benefit and no second claim on that life is payable at all. The UI
 * says so in different words.
 */
export function claimIsApproved(status) {
  const s = String(status || '').trim().toLowerCase();
  return s.indexOf('approv') !== -1 || s.indexOf('paid') !== -1;
}

/**
 * Would a claim in this status still block a new one on the same member?
 *
 * @param {string} status
 * @param {object} [options]
 *   rejectedClaimBlocks  a rejection that stands closes the member, so no further
 *                        claim can be logged for that life. OMK works this way: the
 *                        decision has been taken and a corrected claim goes through
 *                        the pending rejection, not through a second file. Partners
 *                        that let a rejected life be claimed for again leave it off.
 */
export function claimBlocks(status, options) {
  const opts = options || {};
  const s = String(status || '').trim().toLowerCase();
  if (!s) {
    // Unknown status: treat as in flight rather than waving it through.
    return true;
  }
  // A rejection awaiting confirmation is not the last word — it can be sent back and
  // re-decided — so it never stands in the way of a fresh claim.
  if (isPendingRejection(s)) {
    return false;
  }
  if (isAnyRejection(s)) {
    return !!opts.rejectedClaimBlocks;
  }
  for (let i = 0; i < RECLAIMABLE.length; i += 1) {
    if (s.indexOf(RECLAIMABLE[i]) !== -1) {
      return false;
    }
  }
  return true;
}

/** The dependant guid a claim record is filed against, across response shapes. */
function claimDependantGuid(claim) {
  if (!claim || typeof claim !== 'object') {
    return undefined;
  }
  if (claim.dependant_guid) return claim.dependant_guid;
  if (claim.dependant && claim.dependant.guid) return claim.dependant.guid;
  if (claim.member && claim.member.dependant && claim.member.dependant.guid) {
    return claim.member.dependant.guid;
  }
  if (claim.member_guid) return claim.member_guid;
  return undefined;
}

/**
 * The blocking claim for this member, or null.
 *
 * @param {Array} claims       claims already recorded for the customer/policy
 * @param {string} dependantGuid the covered life being claimed for
 * @param {string} [policyGuid]  when given, only claims on this policy are considered
 * @param {object} [options]     as claimBlocks
 */
export function findBlockingClaim(claims, dependantGuid, policyGuid, options) {
  if (!Array.isArray(claims) || !dependantGuid) {
    return null;
  }
  let inFlight = null;
  for (let i = 0; i < claims.length; i += 1) {
    const claim = claims[i];
    if (claimDependantGuid(claim) !== dependantGuid) {
      continue;
    }
    if (policyGuid && claim.policy_guid && claim.policy_guid !== policyGuid) {
      continue;
    }
    if (!claimBlocks(claim.status, options)) {
      continue;
    }
    // An approved claim is the strongest reason and outranks anything still in
    // flight, so it is the one reported however the list happens to be ordered.
    if (claimIsApproved(claim.status)) {
      return claim;
    }
    if (!inFlight) {
      inFlight = claim;
    }
  }
  return inFlight;
}

/** The message shown when a member is blocked. Mirrors the customer journey's wording. */
export function blockingClaimMessage(claim, memberName) {
  const number =
    (claim && (claim.claim_number || claim.number || claim.reference)) || '';
  const named = number ? ' (' + number + ')' : '';
  const status = claim && claim.status ? String(claim.status) : '';
  const who = memberName && String(memberName).trim()
    ? String(memberName).trim()
    : 'This member';

  // A rejection that stands cannot be tracked or corrected, so the wording says the
  // decision is closed rather than pointing at a claim nothing can be done with.
  if (isFinalRejection(status)) {
    return (
      who +
      ' has a rejected claim' +
      named +
      ' on this policy. The rejection is final, so no further claim can be logged ' +
      'for this member.'
    );
  }

  // An approved claim has already paid out on this life, so the wording says the
  // benefit is spent rather than inviting the user to go and track it.
  if (claimIsApproved(status)) {
    return (
      who +
      ' has already been claimed for: claim' +
      named +
      ' on this policy is ' +
      status +
      '. The benefit for this life has been settled and cannot be claimed for a ' +
      'second time.'
    );
  }

  return (
    who +
    ' already has a claim' +
    named +
    ' on this policy' +
    (status ? ', currently ' + status : '') +
    '. A second claim for the same member cannot be submitted — track the existing ' +
    'claim instead.'
  );
}

/** The note shown beside a member the dropdown cannot offer. */
export function blockingClaimLabel(claim) {
  const status = (claim && claim.status) || '';
  if (isFinalRejection(status)) {
    return '(claim rejected — ' + status + ')';
  }
  if (claimIsApproved(status)) {
    return '(already claimed — ' + status + ')';
  }
  return '(claim in progress — ' + status + ')';
}
